// screens/ComplaintsScreen.js - Complaint list (C6), reached from the Reports screen.
// Default period is the last 30 days with an "All" switch (contract decision); filters
// survive opening a dossier and coming back, like Search Orders.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from './HomeScreen';
import { SyncBar, EmptyState, useSmartBack } from '../components/shared';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import api, { getErrorMessage } from '../utils/api';
import {
  buildListParams, canEditComplaints, defaultComplaintFilters, formatDay,
} from '../utils/complaints';

const FILTER_STORE_KEY = 'photoqa_complaint_filters';
const STATUS_CHIPS = ['', 'open', 'incomplete_complaint', 'pending_verdict', 'incomplete_verdict', 'solved', 'cancelled'];

const readSaved = () => {
  try { return JSON.parse(sessionStorage.getItem(FILTER_STORE_KEY)) || null; } catch { return null; }
};

export default function ComplaintsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const back = useSmartBack('/complaints');
  const canEdit = canEditComplaints(user);
  const [filters, setFilters] = useState(() => ({ ...defaultComplaintFilters(), ...(readSaved() || {}) }));
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  useEffect(() => {
    try { sessionStorage.setItem(FILTER_STORE_KEY, JSON.stringify(filters)); } catch {}
  }, [filters]);

  useEffect(() => {
    if (user?.site_id) return; // site users only ever see their own factory
    api.get('/api/sites').then(({ data }) => setSites(data || [])).catch(() => {});
  }, [user?.site_id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/complaints', { params: buildListParams(filters) });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const reset = () => setFilters(defaultComplaintFilters());

  return (
    <div className="screen">
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={back} aria-label={t('complaints.back')}>←</button>
        <h2 style={{ flex: 1, textAlign: 'center' }}>{t('complaints.title')}</h2>
        {canEdit ? (
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/complaints/new')}>
            + {t('complaints.new')}
          </button>
        ) : <div style={{ width: 52 }} />}
      </div>

      <div className="page-content">
        {!canEdit && (
          <div className="form-hint" style={{ marginBottom: 12 }}>{t('complaints.readonly.note')}</div>
        )}

        <div className="search-wrap" style={{ marginBottom: 12 }}>
          <div className="search-icon">🔍</div>
          <input type="search" className="input-field" value={filters.q}
            onChange={(e) => set({ q: e.target.value })}
            placeholder={t('complaints.search.ph')} aria-label={t('complaints.search.ph')} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className={`chip ${filters.all ? '' : 'active'}`} onClick={() => set({ all: false })}>
            {t('complaints.period')}
          </button>
          <input type="date" value={filters.from} max={filters.to} disabled={filters.all}
            aria-label={t('complaints.from')}
            onChange={(e) => set({ from: e.target.value, all: false })}
            className="input-field" style={{ minHeight: 38, width: 'auto', fontSize: '0.875rem' }} />
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{t('complaints.to')}</span>
          <input type="date" value={filters.to} min={filters.from} disabled={filters.all}
            aria-label={t('complaints.to')}
            onChange={(e) => set({ to: e.target.value, all: false })}
            className="input-field" style={{ minHeight: 38, width: 'auto', fontSize: '0.875rem' }} />
          <button type="button" className={`chip ${filters.all ? 'active' : ''}`} onClick={() => set({ all: true })}>
            {t('complaints.all')}
          </button>
          {!user?.site_id && sites.length > 1 && (
            <select value={filters.factory} onChange={(e) => set({ factory: e.target.value })}
              className="input-field" aria-label={t('complaints.factory')}
              style={{ minHeight: 38, width: 'auto', maxWidth: 180, fontSize: '0.875rem' }}>
              <option value="">{t('complaints.allfactories')}</option>
              {sites.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          )}
        </div>

        <div className="chip-row" style={{ marginBottom: 16 }}>
          {STATUS_CHIPS.map((s) => (
            <button key={s || 'any'} type="button" className={`chip ${filters.status === s ? 'active' : ''}`}
              onClick={() => set({ status: s })}>
              {s === '' ? t('complaints.status.any') : s === 'open' ? t('complaints.status.open') : t(`complaints.status.${s}`)}
            </button>
          ))}
          <button type="button" className="chip" onClick={reset} title={t('complaints.reset.title')}>✕ {t('complaints.reset')}</button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><div className="spinner" /></div>
        ) : error ? (
          <div className="error-banner">{error}</div>
        ) : rows.length === 0 ? (
          <EmptyState icon="📋" title={t('complaints.empty.title')}
            message={filters.all ? t('complaints.empty.all') : t('complaints.empty.period')}
            action={canEdit ? (
              <button className="btn btn-primary" onClick={() => navigate('/complaints/new')}>+ {t('complaints.new')}</button>
            ) : null} />
        ) : (
          <>
            <div className="section-title">{rows.length} {rows.length === 1 ? t('complaints.count.one') : t('complaints.count.many')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map((c) => <ComplaintRow key={c.id} c={c} onOpen={() => navigate(`/complaints/${c.id}`)} t={t} />)}
            </div>
          </>
        )}
      </div>

      <BottomNav active="complaints" />
    </div>
  );
}

function ComplaintRow({ c, onOpen, t }) {
  const total = c.lines_total || 0;
  const solved = c.lines_solved || 0;
  const allSolved = total > 0 && solved === total;
  return (
    <button type="button" className="card card-hover" onClick={onOpen}
      style={{ padding: '14px 16px', textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent-primary)', fontSize: '0.9375rem' }}>
              {c.order_number}
            </span>
            {c.factory && <span className="site-badge">{c.factory}</span>}
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[c.customer_name, c.model].filter(Boolean).join(' · ') || t('complaints.noenrichment')}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
            {c.production_date ? (
              <>
                {c.production_date_source === 'delivery' ? t('complaints.deliverydate') : t('complaints.productiondate')}{' '}
                {formatDay(c.production_date)}
                {c.production_date_source === 'delivery' && (
                  <span className="badge badge-warning" style={{ fontSize: '0.625rem', marginLeft: 6 }}>{t('complaints.fallback')}</span>
                )}
              </>
            ) : t('complaints.noproductiondate')}
            {' · '}{t('complaints.registered')} {formatDay(c.created_at)}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <span className={`badge ${allSolved ? 'badge-success' : total === 0 ? 'badge-secondary' : 'badge-warning'}`}>
            {t('complaints.summary').replace('{solved}', solved).replace('{total}', total)}
          </span>
        </div>
      </div>
    </button>
  );
}
